import React, { useCallback, useState } from 'react'
import styled from 'styled-components/macro'
import { useNavigate, useLocation } from 'react-router-dom'
import { PiCaretDown } from 'react-icons/pi'
import { useLeftNavStore } from './left-nav-store'
import type { NavSection } from './types'

const NavGroupWrapper = styled.div`
  margin: 0;
  padding: 0;
`

const NavGroupHeader = styled.button<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 36px;
  padding: 0 8px 0 20px;
  border: none;
  background: transparent;
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease-in-out;

  &:hover {
    background: rgba(165, 190, 175, 0.5);
  }

  svg {
    transition: transform 0.2s ease-in-out;
    transform: ${({ $isExpanded }) =>
      $isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
    color: #07311e;
  }
`

const NavGroupLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #191d23;
  line-height: 1.4;
`

const NavGroupItems = styled.div<{ $isExpanded: boolean }>`
  display: ${({ $isExpanded }) => ($isExpanded ? 'flex' : 'none')};
  flex-direction: column;
  gap: 6px;
  padding-left: 12px;
`

const NavSubItem = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  height: 36px;
  padding: 0 4px 0 20px;
  border: none;
  background: ${({ $isActive }) => ($isActive ? '#A5BEAF' : 'transparent')};
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease-in-out;

  &:hover {
    background: ${({ $isActive }) =>
      $isActive ? '#A5BEAF' : 'rgba(165, 190, 175, 0.5)'};
  }
`

const NavSubItemLabel = styled.span<{ $isActive: boolean }>`
  font-size: 14px;
  font-weight: ${({ $isActive }) => ($isActive ? '700' : '500')};
  color: #191d23;
  line-height: 1.4;
`

export interface NavGroupItem {
  label: string
  path: string
  description?: string
}

interface SlideOutNavGroupProps {
  label: string
  items: NavGroupItem[]
  section?: NavSection
  defaultExpanded?: boolean
  isActiveGroup?: boolean
}

export const SlideOutNavGroup: React.FC<SlideOutNavGroupProps> = ({
  label,
  items,
  section,
  defaultExpanded = false,
  isActiveGroup = false,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { setLastVisited, closeSlideOut } = useLeftNavStore()
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || isActiveGroup)

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  const handleItemClick = useCallback(
    (item: NavGroupItem) => {
      navigate(item.path)
      if (section) {
        setLastVisited(section, item.path)
      }
      closeSlideOut()
    },
    [navigate, section, setLastVisited, closeSlideOut]
  )

  return (
    <NavGroupWrapper>
      <NavGroupHeader $isExpanded={isExpanded} onClick={toggleExpanded}>
        <NavGroupLabel>{label}</NavGroupLabel>
        <PiCaretDown size={16} />
      </NavGroupHeader>
      <NavGroupItems $isExpanded={isExpanded}>
        {items.map((item) => {
          const isActive = location.pathname.startsWith(item.path)
          return (
            <NavSubItem
              key={item.path}
              $isActive={isActive}
              onClick={() => handleItemClick(item)}
              aria-current={isActive ? 'page' : undefined}
            >
              <NavSubItemLabel $isActive={isActive}>
                {item.label}
              </NavSubItemLabel>
            </NavSubItem>
          )
        })}
      </NavGroupItems>
    </NavGroupWrapper>
  )
}
